<template>
  <div v-if="haveBookingRequest">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <booking-campaign-banner v-if="booking && booking.campaign" :campaign="booking.campaign" />
        <div class="row flex align-center">
          <div class="col-sm-6">
            <h1>{{ $t('Request') }} #{{ booking.id | padded('0', 5) }}</h1>
          </div>
          <div class="col-sm-6">
            <booking-status
              :booking="booking"/>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <booking-request-progress
        v-if="$userIsClient" :booking="booking">
      </booking-request-progress>

      <booking-talent-response
        v-if="$userIsTalent && $userIsActivated" :booking="booking">
      </booking-talent-response>

      <booking-responses-list
        v-if="userCanSeeBookingResponses" :booking="booking">
      </booking-responses-list>

      <booking-talent-notifications v-if="adminCanSeeTalentNotifications"
        :booking="booking"
        :recommendations="recommendations">
      </booking-talent-notifications>

      <booking-talent-recommendations
        v-if="$userIsAdmin && bookingTypeIsBookingRequest && bookingIsPending"
        :booking="booking"
        :recommendations="recommendations">
      </booking-talent-recommendations>

      <booking-manager-notifications
        v-if="adminCanSeeTalentNotifications" :booking="booking">
      </booking-manager-notifications>

      <booking-details
        :booking="booking"
        :property-mutator="setRequestProperty">
      </booking-details>

      <booking-client-details
        v-if="($userIsAdmin || $userIsClient) || (($userIsTalent || $userIsManager) && $showClientDetailsBookingModuleEnabled)"
        :booking="booking">
      </booking-client-details>

      <booking-tag-targeting
        v-if="canViewRegionalAdminOptions && $billboardModuleEnabled" :booking="booking"/>

      <booking-notes
        v-if="$userIsAdmin" :booking="booking">
      </booking-notes>

      <request-admin-options
        v-if="(!$vlastMode || (monetaryCurrency && booking.budget_per_star > 0)) && canViewRegionalAdminOptions"
        :booking="booking"
        :property-mutator="setRequestProperty">
      </request-admin-options>

      <booking-sales-pipeline key="1" v-if="$userIsAdmin && !$vlastMode" :booking="booking"/>

      <booking-history
        v-if="$userIsAdmin" :booking="booking">
      </booking-history>

      <div v-if="!$vlastMode">
        <booking-source v-if="canViewRegionalAdminOptions"
          :booking="booking">
        </booking-source>
      </div>

      <booking-search-details-list
        v-if="$userIsClient" :booking="booking">
      </booking-search-details-list>

      <booking-download-pdf
        v-if="$userIsAdmin || $userIsIntern" :booking="booking">
      </booking-download-pdf>

      <booking-upload-files
        v-if="booking.can_upload_files" :booking="booking">
      </booking-upload-files>

      <div class="dashboard-block button-bank" v-if="(canViewRegionalAdminOptions || $userIsClient)">
        <router-link v-if="userCanEditBookingRequest" :to="{ name: 'requests.edit', params: { id } }" class="btn btn-info">
          <i class="fa fa-pencil"></i> &nbsp; Edit {{ !booking.is_offer ? $t('Request') : `Offer` }}
        </router-link>
        <router-link v-if="userCanDuplicateBookingRequest" :to="{ name: 'requests.duplicate', params: { id } }" class="btn btn-info">
          <i class="fa fa-clone"></i> &nbsp; Duplicate {{ !booking.is_offer ? $t('Request') : `Offer` }}
        </router-link>
        <ladda-button
          v-if="userCanWithdrawBookingRequest"
          class="btn btn-danger"
          :click="handleWithdrawBookingRequestCallback">
          <i class="fa fa-close"></i> &nbsp; Withdraw {{ !booking.is_offer ? $t('Request') : `Offer` }}
        </ladda-button>
        <ladda-button
          v-if="userCanReactivateBookingRequest"
          class="btn btn-info"
          :click="handleReactivateBookingRequestCallback">
          <i class="fa fa-refresh"></i> &nbsp;
          <span v-if="bookingIsDeclined">Reactivate Pending {{ !booking.is_offer ? $t('Request') : `Offer` }}</span>
          <span v-else>Reactivate {{ !booking.is_offer ? $t('Request') : `Offer` }}</span>
        </ladda-button>
      </div>
    </div>
    <request-client-withdraw-reason-modal
      ref="modal"
      @input="handleWithdrawRequestReason"/>
    <booking-terms-and-conditions-modal-no-action
      v-if="($userIsAdmin || $userIsClient)"
      :booking="booking"/>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { helpers } from 'mixins/bookings'
import { alert } from 'utils'
import BookingRequestProgress from 'components/booking/BookingRequestProgress'
import BookingDetails from 'components/booking/BookingDetails'
import BookingStatus from 'components/booking/BookingStatus'
import BookingClientDetails from 'components/booking/BookingClientDetails'
import BookingSearchDetailsList from 'components/booking/BookingSearchDetailsList'
import BookingTalentResponse from 'components/booking/BookingTalentResponse'
import BookingResponsesList from 'components/booking/BookingResponsesList'
import BookingTalentNotifications from 'components/booking/BookingTalentNotifications'
import BookingTalentRecommendations from 'components/booking/BookingTalentRecommendations'
import BookingDownloadPdf from 'components/booking/BookingDownloadPdf'
import BookingNotes from 'components/booking/BookingNotes'
import BookingSource from 'components/booking/BookingSource'
import RequestAdminOptions from 'components/booking/request/RequestAdminOptions'
import RequestClientWithdrawReasonModal from 'components/booking/request/RequestClientWithdrawReasonModal'
import BookingManagerNotifications from 'components/booking/BookingManagerNotifications'
import BookingTermsAndConditionsModalNoAction from 'components/booking/BookingTermsAndConditionsModalNoAction'
import BookingHistory from 'components/booking/BookingHistory'
import BookingUploadFiles from 'components/booking/BookingUploadFiles'
import BookingTagTargeting from 'components/booking/BookingTagTargeting'
import BookingSalesPipeline from 'components/booking/BookingSalesPipeline'
import BookingCampaignBanner from '../BookingCampaignBanner'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  props: ['id'],

  data () {
    return {
      withdrawRequestPromise: null,
      withdrawRequestReason: null
    }
  },

  components: {
    BookingCampaignBanner,
    BookingSalesPipeline,
    BookingHistory,
    BookingRequestProgress,
    BookingDetails,
    BookingStatus,
    BookingClientDetails,
    BookingSearchDetailsList,
    BookingTalentResponse,
    BookingResponsesList,
    BookingTalentNotifications,
    BookingTalentRecommendations,
    BookingDownloadPdf,
    BookingNotes,
    BookingSource,
    RequestAdminOptions,
    RequestClientWithdrawReasonModal,
    BookingTermsAndConditionsModalNoAction,
    BookingManagerNotifications,
    BookingUploadFiles,
    BookingTagTargeting
  },

  computed: {
    ...mapGetters('booking/request', ['haveBookingRequest']),
    ...mapState('booking/request', {
      booking: 'request',
      recommendations: 'recommendations'
    }),

    adminCanSeeTalentNotifications () {
      return !this.$vlastMode && this.$userIsAdmin && this.bookingTypeIsBookingRequest && this.bookingIsPending
    },

    userCanSeeBookingResponses () {
      if (this.$userIsAdmin || this.$userIsClient) {
        return this.bookingTypeIsBookingRequest &&
            (this.bookingIsPending || this.bookingIsStandardPackage)
      } else if (this.$userIsManager) {
        return true
      }

      return false
    },

    userCanWithdrawBookingRequest () {
      return (this.$userIsAdmin || this.$userIsClient) &&
          this.bookingIsNotWithdrawn &&
          this.bookingIsNotDeclined
    },

    userCanReactivateBookingRequest () {
      return (this.$userIsAdmin) &&
          (this.bookingIsWithdrawn || this.bookingIsDeclined)
    },

    userCanDuplicateBookingRequest () {
      return this.$userIsAdmin || this.$userIsClient
    },

    userCanEditBookingRequest () {
      if (this.bookingIsDeclined || this.bookingIsWithdrawn) {
        return false
      }

      return this.$userIsAdmin ||
          this.bookingIsVetting
    },

    canViewRegionalAdminOptions () {
      return this.$userIsBookingRegionalAdmin(this.$user, this.booking)
    }
  },

  destroyed () {
    this.clearAllFilters()
    this.resetBookingRequest()
  },

  created () {
    this.getBookingRequest({ id: this.id })
      .then(this.redirectIfBookingRequestIsVetting)
      .then(() => {
        this.trackEvent(this.analytics.V2.JobOpenEvent)
      })
      .catch((error) => {
        this.handleCaughtHttpExceptionForBooking(error, 'The ' + this.$t('booking request') + ' could not be found.')
      })
    if (this.$userIsAdmin) {
      this.getBookingRecommendations({ id: this.id })
    }
  },

  methods: {
    ...mapActions('booking/request', ['getBookingRequest', 'getBookingRecommendations', 'withdrawBookingRequest', 'reactivateBookingRequest', 'resetBookingRequest', 'setRequestProperty']),
    ...mapActions('filtering', ['clearAllFilters']),

    redirectIfBookingRequestIsVetting () {
      if (this.$userIsAdmin && this.bookingIsVetting) {
        this.$router.replace({ name: 'vetting.show', params: { id: this.booking.id } })
      }
    },

    /**
       * Handle withdrawing a booking request.
       *
       * @return Promise
       */
    handleWithdrawBookingRequestCallback () {
      this.withdrawRequestReason = null
      const confirmAlertOptions = {
        title: 'Withdraw Booking Request',
        text: 'Are you sure you want to withdraw this ' + this.$t('booking request') + '?'
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          this.withdrawRequestReason = null
          window.$(this.$refs.modal.$el).modal('show')
        })
    },

    handleWithdrawRequestReason (reason) {
      this.withdrawRequestReason = reason
      this.trackEvent(this.analytics.V2.JobWithdrawEvent)

      this.withdrawBookingRequest({ id: this.booking.id, reason: this.withdrawRequestReason })
        .then((response) => {
          alert.timedSuccess({
            title: this.$t('Booking request') + '  Withdrawn',
            text: 'Your ' + this.$t('booking request') + ' has been withdrawn.'
          })
        })
    },

    /**
       * Handle withdrawing a booking request.
       *
       * @return Promise
       */
    handleReactivateBookingRequestCallback () {
      let confirmAlertOptions = {
        title: 'Reactivate Booking Request',
        text: 'Are you sure you want to reactivate this ' + this.$t('booking request') + '?'
      }
      if (this.bookingIsDeclined) {
        confirmAlertOptions = {
          title: 'Reactivate Pending Request',
          text: 'Are you sure you want to reactivate this pending ' + this.$t('request') + '?'
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          this.reactivateBookingRequest({ id: this.booking.id })
            .then((response) => {
              alert.timedSuccess({
                title: this.$t('Booking Request') + '  Reactivated',
                text: 'Your ' + this.$t('booking request') + ' has been reactivated.'
              })
            }).then(this.handleRedirectToVettingRequest)
        })
    },

    /**
       * Handle redirect to the "requests.show" route after vetting has succeeded.
       *
       * @return void
       */
    handleRedirectToVettingRequest () {
      if (this.booking.status === 'vetting') {
        this.$router.push({ name: 'vetting.show', params: { id: this.booking.id } })
      }
    }
  }
}
</script>
